// src/Components/partners/ActivePartnersTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pause, Trash2, BadgeCheck, Badge, Globe } from 'lucide-react';
import { partnersAPI } from '../../services/api';
import { Skeleton, EmptyState } from '../dashboard/design-system';
import PartnerFormModal from './PartnerFormModal';

export default function ActivePartnersTab() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [actioningId, setActioningId]   = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnersAPI.listPartners();
      if (res.success) setPartners(res.data.partners || []);
    } catch (err) {
      console.error('Failed to load partners:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleAddPartner = async (form) => {
    setSubmitting(true);
    try {
      await partnersAPI.createPartner(form);
      setShowAddModal(false);
      await fetchPartners();
    } catch (err) {
      alert(`Create failed: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVerified = async (partner) => {
    setActioningId(partner.id);
    try {
      await partnersAPI.updatePartner(partner.id, { is_verified: !partner.is_verified });
      setPartners((prev) => prev.map((p) => p.id === partner.id ? { ...p, is_verified: !p.is_verified } : p));
    } catch (err) {
      alert(`Update failed: ${err}`);
    } finally {
      setActioningId(null);
    }
  };

  const handlePause = async (id) => {
    setActioningId(id);
    try {
      await partnersAPI.pausePartner(id);
      setPartners((prev) => prev.filter((p) => p.id !== id)); // drops off "approved" list immediately
    } catch (err) {
      alert(`Pause failed: ${err}`);
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (id) => {
    setActioningId(id);
    try {
      await partnersAPI.deletePartner(id);
      setPartners((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      alert(`Delete failed: ${err}`);
    } finally {
      setActioningId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{partners.length} active partners</p>
        <button onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700 transition">
          <Plus size={14} /> Add Partner Manually
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : partners.length === 0 ? (
        <EmptyState icon={Globe} title="No active partners yet" sub="Approve an application or add one manually" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="pb-3 font-semibold">Name</th>
                <th className="pb-3 font-semibold">Category</th>
                <th className="pb-3 font-semibold">Country</th>
                <th className="pb-3 font-semibold">Source</th>
                <th className="pb-3 text-center font-semibold">Verified</th>
                <th className="pb-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {partners.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  <td className="py-3 font-medium">{p.name}</td>
                  <td className="py-3">
                    <span className="px-2 py-0.5 bg-sky-50 dark:bg-sky-900/20 text-sky-600 text-xs rounded-full">
                      {p.category || '—'}
                    </span>
                  </td>
                  <td className="py-3 text-gray-500">{p.country || '—'}</td>
                  <td className="py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      p.source === 'manual_admin' ? 'bg-purple-50 text-purple-600' : 'bg-emerald-50 text-emerald-600'
                    }`}>
                      {p.source === 'manual_admin' ? 'Manual' : 'App'}
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <button disabled={actioningId === p.id} onClick={() => toggleVerified(p)}
                      className="mx-auto block">
                      {p.is_verified
                        ? <BadgeCheck size={16} className="text-emerald-500" />
                        : <Badge size={16} className="text-gray-300" />}
                    </button>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <button disabled={actioningId === p.id} onClick={() => handlePause(p.id)}
                        title="Pause — drops off the app immediately, doesn't delete"
                        className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition disabled:opacity-40">
                        <Pause size={14} />
                      </button>
                      <button disabled={actioningId === p.id} onClick={() => setConfirmDeleteId(p.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 transition disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <PartnerFormModal
          title="Add Partner Manually"
          prefill={null}
          submitting={submitting}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddPartner}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Delete this partner?</h3>
            <p className="text-sm text-gray-500 mb-5">
              This permanently removes them from the partners table. Consider Pause instead if you might reinstate them later.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition">
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDeleteId)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}